import { v4 as uuidv4 } from 'uuid';
import { openDB, IDBPDatabase } from 'idb';
import SentryService from '../../services/SentryService';

export type LogEntry = {
  timestamp: number;
  event: string;
  details?: any;
};

export type RecordingChunk = {
  id: string;
  position?: number;
  start_timestamp_seconds: number;
  end_timestamp_seconds: number;
  upload_status: 'pending' | 'error' | 'success';
  chunk_data?: {
    chunk_size: number;
    chunk_type: string;
  };
  s3_object_id?: string;
  transcription_session_id?: string;
};

export type EndSessionStatus = {
  status: 'success' | 'retrying' | 'failed';
  failedChunks?: RecordingChunk[];
};

export class RecordingSession {
  id: string;
  start_timestamp?: number;
  end_timestamp?: number;
  transcription_session_id?: string;
  successfully_uploaded?: boolean;
  recording_ended?: boolean;
  recording_title?: string;
  chunks: RecordingChunk[];
  logs: LogEntry[] = [];

  // Helper method to get database connection with proper initialization
  private static async getDatabase(): Promise<IDBPDatabase> {
    try {
      // Open with a proper version upgrade path
      return await openDB('SmartScribeRecordingSessions', 1, {
        upgrade(db, oldVersion, newVersion) {
          // If the object store doesn't exist, create it
          if (!db.objectStoreNames.contains('recordingSessions')) {
            db.createObjectStore('recordingSessions');
          }
        },
      });
    } catch (error) {
      console.error('Error accessing database:', error);
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'RecordingSession' },
        extra: { method: 'getDatabase' },
      });
      throw error;
    }
  }

  // Helper method to recreate database when object stores are corrupted
  public static async recreateDatabase(): Promise<IDBPDatabase> {
    try {
      // Delete the existing database
      await new Promise<void>((resolve, reject) => {
        const deleteReq = indexedDB.deleteDatabase('SmartScribeRecordingSessions');
        deleteReq.onsuccess = () => resolve();
        deleteReq.onerror = () => reject(deleteReq.error);
        deleteReq.onblocked = () => {
          console.warn('Database deletion blocked, continuing anyway');
          resolve();
        };
      });

      SentryService.addBreadcrumb({
        category: 'recording.storage',
        message: 'Recreated corrupted IndexedDB database',
        level: 'info',
      });

      // Create a fresh database
      return await RecordingSession.getDatabase();
    } catch (error) {
      console.error('Error recreating database:', error);
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'RecordingSession' },
        extra: { method: 'recreateDatabase' },
      });
      throw error;
    }
  }

  constructor({ recording_title }: { recording_title?: string }) {
    this.id = String(uuidv4());
    this.recording_title = recording_title;
    this.chunks = [];
    SentryService.addBreadcrumb({
      category: 'recording.session',
      message: 'Recording session created',
      level: 'info',
      data: { recordingSessionId: this.id },
    });
    this.saveToStorage();
  }

  static async cleanupCurrentSession() {
    SentryService.addBreadcrumb({
      category: 'recording.session',
      message: 'Cleaning up current session',
      level: 'info',
    });
    // remove currentRecordingSession from storage
    await this.clearAllObjectStores();
  }

  static async clearAllObjectStores() {
    try {
      const db = await openDB('SmartScribeAudioChunks', 1, {
        upgrade(db, oldVersion, newVersion) {
          // No need to create stores here, we're just clearing existing ones
        },
      });

      // Only proceed if there are object stores
      if (db.objectStoreNames.length > 0) {
        const tx = db.transaction(db.objectStoreNames, 'readwrite');

        Array.from(db.objectStoreNames).forEach(storeName => {
          const store = tx.objectStore(storeName);
          store.clear();
        });

        await tx.done;
      }

      SentryService.addBreadcrumb({
        category: 'recording.storage',
        message: 'Cleared all object stores',
        level: 'info',
      });
    } catch (error) {
      console.error('Error clearing object stores:', error);
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'RecordingSession' },
        extra: { method: 'clearAllObjectStores' },
      });
    }
  }

  addMediaStreamInfo(mediaStreamInfo: any) {
    this.addLog('MediaStreamInfo', mediaStreamInfo);

    // Track some key metrics in Sentry context
    if (mediaStreamInfo?.tracks) {
      SentryService.setTransactionAttribute(
        'mediaStreamInfo',
        JSON.stringify({
          streamType: mediaStreamInfo.streamType,
          trackCount: mediaStreamInfo.tracks?.length || 0,
          useSharedMedia: mediaStreamInfo.useSharedMedia || false,
        }),
      );
    }

    this.saveToStorage();
  }

  // Method to add logs
  addLog(event: string, details?: any): void {
    const logEntry: LogEntry = {
      timestamp: Date.now(),
      event,
      details,
    };
    this.logs.push(logEntry);

    // For important events, add breadcrumbs to Sentry
    const importantEvents = [
      'Session Started',
      'Session Ended',
      'Associated Transcription Session',
      'MediaRecorderCreated',
      'NoAudioDetected',
      'SharedMediaError',
    ];

    if (importantEvents.includes(event)) {
      SentryService.addBreadcrumb({
        category: 'recording.session',
        message: event,
        level: event.includes('Error') ? 'error' : 'info',
        data: details,
      });
    }
  }

  async saveToStorage(): Promise<void> {
    try {
      const db = await RecordingSession.getDatabase();

      // Check if the object store exists before creating a transaction
      if (!db.objectStoreNames.contains('recordingSessions')) {
        SentryService.addBreadcrumb({
          category: 'recording.storage',
          message: "Object store 'recordingSessions' does not exist during save",
          level: 'warning',
          data: {
            sessionId: this.id,
            availableStores: Array.from(db.objectStoreNames),
          },
        });
        console.warn(
          `Object store 'recordingSessions' does not exist in database during save. Available stores: ${Array.from(db.objectStoreNames).join(', ')}`,
        );
        return; // Exit gracefully without throwing
      }

      const tx = db.transaction('recordingSessions', 'readwrite');
      const store = tx.objectStore('recordingSessions');
      await store.put(JSON.stringify(this), `recordingSession:${this.id}`);
      await tx.done;
    } catch (e) {
      console.log('Failed to save recording session to storage', e);

      // Check if this is the specific transaction error we're trying to handle
      if (e instanceof Error && e.message.includes('object stores was not found')) {
        SentryService.addBreadcrumb({
          category: 'recording.storage',
          message: 'IndexedDB object store not found during save transaction',
          level: 'warning',
          data: {
            sessionId: this.id,
            errorMessage: e.message,
            errorType: 'missing_object_store',
          },
        });
        console.warn(
          `Object store missing during save transaction for session ${this.id}:`,
          e.message,
        );
        return; // Exit gracefully instead of throwing
      }

      SentryService.captureException(e, {
        level: 'error',
        tags: { component: 'RecordingSession' },
        extra: { method: 'saveToStorage', sessionId: this.id },
      });
    }
  }

  static async loadFromStorage(id: string): Promise<RecordingSession | null> {
    try {
      const db = await RecordingSession.getDatabase();

      // Check if the object store exists before creating a transaction
      if (!db.objectStoreNames.contains('recordingSessions')) {
        SentryService.addBreadcrumb({
          category: 'recording.storage',
          message: "Object store 'recordingSessions' does not exist",
          level: 'warning',
          data: {
            sessionId: id,
            availableStores: Array.from(db.objectStoreNames),
          },
        });
        console.warn(
          `Object store 'recordingSessions' does not exist in database. Available stores: ${Array.from(db.objectStoreNames).join(', ')}`,
        );
        return null;
      }

      const tx = db.transaction('recordingSessions');
      const store = tx.objectStore('recordingSessions');
      const result = await store.get(`recordingSession:${id}`);

      if (result) {
        const sessionData = JSON.parse(result);

        // Create a new RecordingSession object from the parsed data
        const recordingSession = new RecordingSession({
          recording_title: sessionData.recording_title,
        });

        // Copy properties from the parsed data to the new object
        recordingSession.id = sessionData.id;
        recordingSession.start_timestamp = sessionData.start_timestamp;
        recordingSession.end_timestamp = sessionData.end_timestamp;
        recordingSession.transcription_session_id = sessionData.transcription_session_id;
        recordingSession.successfully_uploaded = sessionData.successfully_uploaded;
        recordingSession.recording_ended = sessionData.recording_ended;
        recordingSession.chunks = sessionData.chunks || [];
        recordingSession.logs = sessionData.logs || [];

        SentryService.addBreadcrumb({
          category: 'recording.session',
          message: 'Loaded recording session from storage',
          level: 'info',
          data: {
            sessionId: id,
            chunksCount: recordingSession.chunks.length,
            hasTranscriptionId: !!recordingSession.transcription_session_id,
          },
        });

        return recordingSession;
      }
    } catch (e) {
      console.log(`Failed to load recording session ${id} from storage`, e);

      // Check if this is the specific transaction error we're trying to handle
      if (e instanceof Error && e.message.includes('object stores was not found')) {
        SentryService.addBreadcrumb({
          category: 'recording.storage',
          message: 'IndexedDB object store not found during transaction',
          level: 'warning',
          data: {
            sessionId: id,
            errorMessage: e.message,
            errorType: 'missing_object_store',
          },
        });
        console.warn(`Object store missing during transaction for session ${id}:`, e.message);
        return null; // Return null gracefully instead of throwing
      }

      SentryService.captureException(e, {
        level: 'error',
        tags: { component: 'RecordingSession' },
        extra: { method: 'loadFromStorage', sessionId: id },
      });
    }
    return null;
  }

  async startSession(): Promise<void> {
    console.log(`RecordingSession:${this.id}: \n\t\tStarting RecordingSession`);
    this.addLog('Session Started', { recording_session_id: this.id });
    this.start_timestamp = Date.now();

    SentryService.addBreadcrumb({
      category: 'recording.session',
      message: 'Recording session started',
      level: 'info',
      data: { sessionId: this.id },
    });

    try {
      // Save the current recording session ID
      const db = await RecordingSession.getDatabase();

      // Check if the object store exists before creating a transaction
      if (!db.objectStoreNames.contains('recordingSessions')) {
        SentryService.addBreadcrumb({
          category: 'recording.storage',
          message: "Object store 'recordingSessions' does not exist during start session",
          level: 'warning',
          data: {
            sessionId: this.id,
            availableStores: Array.from(db.objectStoreNames),
          },
        });
        console.warn(
          `Object store 'recordingSessions' does not exist in database during start session. Available stores: ${Array.from(db.objectStoreNames).join(', ')}`,
        );
        return; // Exit gracefully without throwing
      }

      const tx = db.transaction('recordingSessions', 'readwrite');
      const store = tx.objectStore('recordingSessions');
      await store.put(this.id, 'currentRecordingSession');
      await tx.done;

      // Save the whole session object to storage
      await this.saveToStorage();
    } catch (e) {
      console.log('Failed to start recording session', e);

      // Check if this is the specific transaction error we're trying to handle
      if (e instanceof Error && e.message.includes('object stores was not found')) {
        SentryService.addBreadcrumb({
          category: 'recording.storage',
          message: 'IndexedDB object store not found during start session transaction',
          level: 'warning',
          data: {
            sessionId: this.id,
            errorMessage: e.message,
            errorType: 'missing_object_store',
          },
        });
        console.warn(
          `Object store missing during start session transaction for session ${this.id}:`,
          e.message,
        );
        return; // Exit gracefully instead of throwing
      }

      SentryService.captureException(e, {
        level: 'error',
        tags: { component: 'RecordingSession' },
        extra: { method: 'startSession', sessionId: this.id },
      });
    }
  }

  async endSession(): Promise<EndSessionStatus> {
    console.log(`RecordingSession:${this.id}: \n\t\tEnding RecordingSession`);

    // Create a span for ending the recording session
    const span = SentryService.startInactiveSpan(
      'recording.session.end',
      `End recording session ${this.id}`,
    );
    try {
      span?.setAttribute('sessionId', this.id);

      // set end timestamp
      this.end_timestamp = Date.now();
      this.recording_ended = true;

      if (this.start_timestamp) {
        const duration = (this.end_timestamp - this.start_timestamp) / 1000; // in seconds
        span?.setAttribute('duration', duration);
        SentryService.setMeasurement('session_duration', duration, 'second');
      }

      span?.setAttribute('chunkCount', this.chunks.length);

      // this.printChunkStatus();

      // check if all chunks have been uploaded
      const failedChunks = this.getFailedChunks();
      if (failedChunks.length > 0) {
        // Add a log that the session ended
        this.addLog('Session Ended', { status: 'failed' });

        span?.setStatus({ code: 2 });
        span?.setAttribute('failedChunks', failedChunks.length);

        SentryService.addBreadcrumb({
          category: 'recording.session',
          message: 'Session ended with failed chunks',
          level: 'warning',
          data: { sessionId: this.id, failedChunks: failedChunks.length },
        });

        this.saveToStorage();
        return { status: 'failed', failedChunks };
      } else {
        this.addLog('Session Ended', { status: 'success' });

        span?.setStatus({ code: 1 });

        SentryService.addBreadcrumb({
          category: 'recording.session',
          message: 'Session ended successfully',
          level: 'info',
          data: { sessionId: this.id },
        });

        this.saveToStorage();
        return { status: 'success', failedChunks };
      }
    } catch (error) {
      console.error(`Error ending session ${this.id}:`, error);
      SentryService.logEvent('Error ending recording session.', {
        level: 'error',
        extra: { sessionId: this.id, error },
      });

      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));
      throw error;
    } finally {
      span?.end();
    }
  }

  associateTranscriptionSession(transcriptionSessionId: string) {
    this.transcription_session_id = transcriptionSessionId;
    this.addLog('Associated Transcription Session', { transcriptionSessionId });

    SentryService.addBreadcrumb({
      category: 'recording.session',
      message: 'Associated with transcription session',
      level: 'info',
      data: {
        recordingSessionId: this.id,
        transcriptionSessionId,
      },
    });

    this.saveToStorage();
  }

  updateRecordingTitle(recording_title: string) {
    this.recording_title = recording_title;
    this.addLog('Updated Recording Title', { recording_title });
    this.saveToStorage();
  }

  addChunkToRecordingSession(chunk: RecordingChunk) {
    try {
      console.log(`RecordingSession:${this.id}: \n\t\tAdding Chunk ${chunk.id}`);
      this.addLog('Added Chunk', { chunk });
      chunk.position = this.chunks.length;
      this.chunks.push(chunk);

      SentryService.addBreadcrumb({
        category: 'recording.chunk',
        message: `Added chunk ${chunk.id} to session`,
        level: 'info',
        data: {
          chunkId: chunk.id,
          position: chunk.position,
          sessionId: this.id,
        },
      });

      this.saveToStorage();
      return chunk;
    } catch (error) {
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'RecordingSession' },
        extra: {
          method: 'addChunkToRecordingSession',
          sessionId: this.id,
          chunkId: chunk.id,
        },
      });
      throw error;
    }
  }

  updateChunkUploadStatus(
    chunkId: string,
    s3_object_id: string | undefined,
    successfully_uploaded: boolean,
  ) {
    console.log(`RecordingSession:${this.id}: \n\t\tUpdating UploadStatus of Chunk ${chunkId}`);
    this.addLog('Updated Chunk Upload Status', {
      chunk_id: chunkId,
      upload_status: successfully_uploaded,
      s3_object_id,
    });
    const chunkIndex = this.chunks.findIndex(chunk => chunk.id === chunkId);

    if (chunkIndex === -1) {
      console.error(`Chunk ${chunkId} not found in RecordingSession ${this.id}`);

      SentryService.captureMessage(
        `Chunk ${chunkId} not found in RecordingSession ${this.id}`,
        'warning',
      );
      return;
    }

    this.chunks[chunkIndex].s3_object_id = s3_object_id;
    this.chunks[chunkIndex].upload_status = successfully_uploaded ? 'success' : 'error';

    if (!successfully_uploaded) {
      SentryService.addBreadcrumb({
        category: 'recording.chunk',
        message: `Chunk upload failed: ${chunkId}`,
        level: 'error',
        data: {
          chunkId,
          s3_object_id,
          position: this.chunks[chunkIndex].position,
        },
      });
    }

    this.saveToStorage();
  }

  getFailedChunks() {
    const failedChunks = this.chunks.filter(chunk => chunk.upload_status === 'error');

    if (failedChunks.length > 0) {
      this.successfully_uploaded = false;
      this.addLog('Got Failed Chunks', {
        failed_chunks: failedChunks.length,
      });
      console.log(`RecordingSession:${this.id}: \n\t\tFailed chunks:`, failedChunks);

      // Track detailed info about failed chunks
      const failureDetails = failedChunks.map(chunk => ({
        chunkId: chunk.id,
        position: chunk.position,
        size: chunk.chunk_data?.chunk_size,
      }));

      SentryService.setTransactionAttribute('failedChunks', JSON.stringify(failureDetails));
      SentryService.setMeasurement('failed_chunk_count', failedChunks.length, 'count');
    } else {
      this.successfully_uploaded = true;
      console.log(`RecordingSession:${this.id}: \n\t\tAll chunks uploaded successfully`);
    }

    this.saveToStorage();

    return failedChunks;
  }

  getPendingChunks() {
    const pendingChunks = this.chunks.filter(chunk => chunk.upload_status === 'pending');

    return pendingChunks;
  }

  getEndTimestampOfLastChunk() {
    // Check if there are any chunks
    if (this.chunks.length === 0) {
      return null;
    }

    // Get the last chunk
    const lastChunk = this.chunks[this.chunks.length - 1];

    // Return the end timestamp of the last chunk
    return Math.round(lastChunk.end_timestamp_seconds);
  }

  printChunkStatus() {
    // DEBUG print all chunks
    console.log(`\n\n\n\n\nRecordingSession:${this.id}:\n\t\t`);
    console.log(`\t\t`, this);
    for (const chunk of this.chunks) {
      console.log(`\t\t\t\t`, chunk.id, chunk.upload_status, chunk.chunk_data);
    }
  }

  toHumanReadableDate(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleString();
  }

  generateLogsString() {
    return JSON.stringify(
      {
        id: this.id,
        logs: this.logs,
        chunks: this.chunks.map(chunk => ({
          id: chunk.id,
          position: chunk.position,
          upload_status: chunk.upload_status,
          s3_object_id: chunk.s3_object_id,
        })),
        transcription_session_id: this.transcription_session_id,
        start_timestamp: this.start_timestamp
          ? this.toHumanReadableDate(this.start_timestamp)
          : undefined,
        end_timestamp: this.end_timestamp
          ? this.toHumanReadableDate(this.end_timestamp)
          : undefined,
        chunk_count: this.chunks.length,
      },
      null,
      2,
    );
  }

  public printLogs() {
    console.log(this.generateLogsString());
  }
}
